import { cloneDeep } from 'lodash';

import * as K from 'common/constants';
import { filterList } from 'common/util/array';
import { buildUrl } from 'common/util/location';
import { addTermToFilter } from 'common/util/filter/filter';
import { UrlParams } from 'common/types';
import { Host } from 'common/graphqlTypes';

// TODO: move these types to common/types once logSources types with clashing names are fixed.
export type Cluster = {
  controllers: Array<KubeController>;
  dashboard: Dashboard;
  name: string;
};

export type KubeController = {
  labels: string;
  name: string;
  pods: Array<KubePod>;
  dashboard?: Dashboard;
  kind?: string;
};

export type KubePod = {
  name: string;
};

export type Dashboard = {
  name: string;
  urlTemplate: string;
};

/**
 * Filter function used to filter title, podCount, dashboard title, and labels in each controller.
 *
 * @param clusters      list of clusters
 * @param filterText    text to filter by
 *
 * @returns list of all clusters with their controllers filtered using the filter text
 */
export const filterClusters = (clusters: Array<Cluster>, filterText: string): Array<Cluster> =>
  (clusters || []).map((cluster) => {
    const clusterCopy = cloneDeep(cluster);
    clusterCopy.controllers = filterList(clusterCopy.controllers, filterText, 0, (controller) => {
      const strings = [controller.name, controller.labels, 'all events', controller.pods.length.toString()];
      if (controller.dashboard) {
        strings.push(controller.dashboard.name);
      }
      return strings;
    });
    return clusterCopy;
  });

/**
 * Filter function used to filter server name, ip, logs, and dashboards.
 *
 * @param hosts        list of hosts
 * @param filterText   text to filter by
 *
 * @return list of hosts filtered using filter text
 */
export const filterHosts = (hosts: Array<Host>, filterText: string): Array<Host> =>
  filterList(hosts || [], filterText, 0, ({ dashboards = [], ipAddress = '', logFiles, name }) =>
    [name, ipAddress].concat(logFiles).concat(dashboards.map((dashboard: Dashboard) => dashboard.name)),
  );

/**
 * Creates a link to the search page and populates the filter value.
 *
 * @param filter           list of hosts
 * @param clusterName      text to filter by
 * @param controllerName   name of the controller
 *
 * @return orchestration link
 */
export const createOrchestrationLink = (
  filter: string,
  clusterName: string | null,
  controllerName: string | null,
  controllerKind: string | null | undefined,
): string => {
  const urlParams = {} as UrlParams;

  if (clusterName) {
    urlParams[K.URL_PARAM.KUBERNETES_CLUSTER] = clusterName;
  }

  if (controllerKind && controllerName) {
    const queryTerm = K.FACET_TERM_TO_QUERY_TERM[controllerKind as K.FACET_TERM];
    if (queryTerm) {
      urlParams[queryTerm as unknown as K.URL_PARAM] = controllerName;
    }
  }

  urlParams[K.URL_PARAM.FILTER] = addTermToFilter({
    filter,
    field: 'scalyr-category',
    op: '=',
    value: "'log'",
  });

  return buildUrl(K.ROUTE.EVENTS, urlParams);
};
